import { motion } from "framer-motion";
import { MapPin, Mail, Phone, MessageCircle } from "lucide-react";
import { Seo } from "@/components/Seo";
import { EnquiryForm } from "@/components/EnquiryForm";
import { useSettings, whatsappLink } from "@/context/SettingsContext";

export default function Contact() {
  const settings = useSettings();
  const wa = whatsappLink(settings.whatsapp_number, "Hello Shineni India, I have a bulk denim requirement.");

  return (
    <div data-testid="contact-page">
      <Seo
        title="Contact Us — Shineni India, Denim Manufacturer Faridabad"
        description="Contact Shineni India (Madneel Ventures Pvt. Ltd.), Faridabad, Haryana for wholesale, export and private-label denim enquiries."
      />

      <section className="border-b border-border bg-white">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20">
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-gold">We reply to every serious enquiry</p>
          <h1 className="mt-3 font-display text-4xl font-semibold text-indigo sm:text-5xl">Contact us</h1>
        </div>
      </section>

      <section className="mx-auto grid max-w-7xl gap-12 px-4 py-16 sm:px-6 lg:grid-cols-5">
        <motion.div
          initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          className="space-y-6 lg:col-span-2"
        >
          <div className="border border-border bg-white p-8">
            <h2 className="font-display text-xl font-semibold text-indigo">Shineni India</h2>
            <p className="mt-1 text-sm text-foreground/70">A brand operated by Madneel Ventures Pvt. Ltd.</p>
            <ul className="mt-6 space-y-4 text-sm">
              <li className="flex items-start gap-3">
                <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
                <span>{settings.address || "Faridabad, Haryana, India"}</span>
              </li>
              {settings.business_email && (
                <li className="flex items-start gap-3">
                  <Mail className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
                  <a className="hover:text-indigo" href={`mailto:${settings.business_email}`} data-testid="contact-email">{settings.business_email}</a>
                </li>
              )}
              {settings.phone && (
                <li className="flex items-start gap-3">
                  <Phone className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
                  <a className="hover:text-indigo" href={`tel:${settings.phone.replace(/[^0-9+]/g, "")}`} data-testid="contact-phone">{settings.phone}</a>
                </li>
              )}
            </ul>
            {wa && (
              <a href={wa} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-cta"
                className="mt-8 flex items-center justify-center gap-2 rounded-sm bg-[#1FA855] px-6 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-[#178a45]">
                <MessageCircle className="h-4 w-4" /> Chat on WhatsApp
              </a>
            )}
            {!settings.business_email && !settings.phone && !wa && (
              <p className="mt-6 border-l-2 border-gold pl-4 text-sm text-foreground/70">
                Our direct lines are being set up — the fastest way to reach us right now is the
                enquiry form. We respond on your email or WhatsApp.
              </p>
            )}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}
          className="lg:col-span-3"
        >
          <EnquiryForm variant="general" title="General enquiry" subtitle="For anything that isn't a wholesale, export or quote request." />
        </motion.div>
      </section>
    </div>
  );
}
